import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Inbox, Plus, RefreshCw, ChevronRight, CheckCircle,
  Clock, XCircle, Ban, Settings, ExternalLink,
} from "lucide-react";

const STATUS_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", Icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-green-100 text-green-700", Icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700", Icon: XCircle },
  cancelada: { label: "Cancelada", color: "bg-slate-100 text-slate-500", Icon: Ban },
  cerrada: { label: "Cerrada", color: "bg-slate-100 text-slate-600", Icon: CheckCircle },
};

const TYPE_LABELS = {
  compra: "Requisición de compra",
  gasto: "Gasto",
  reembolso: "Reembolso",
  anticipo: "Anticipo",
  vacante: "Vacante",
  viatico: "Viáticos",
  mantenimiento: "Mantenimiento",
  compra_externa: "Compra externa",
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function OpsSolicitudes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mias");
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mineRes, pendingRes] = await Promise.all([
        api.get("/ops/requests/mine"),
        api.get("/ops/requests/pending-approval"),
      ]);
      setMine(mineRes.data || []);
      setPending(pendingRes.data?.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openRow = (row) => {
    if (row.mirror_readonly && row.deep_link) { navigate(row.deep_link); return; }
    navigate(`/ops/solicitudes/${row.id}`);
  };

  const rows = (tab === "mias" ? mine : pending)
    .filter((r) => !filterType || r.type === filterType)
    .filter((r) => !filterStatus || r.status === filterStatus);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ops-solicitudes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-yellow-500" /> Solicitudes internas
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Buzón único: mis solicitudes y lo que espera mi aprobación</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/ops/solicitudes/nueva")}
            className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors"
            data-testid="btn-nueva-solicitud">
            <Plus className="w-4 h-4" /> Nueva solicitud
          </button>
          {user?.role === "corporate_admin" && (
            <button onClick={() => navigate("/ops/admin/solicitudes/reglas")}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" title="Reglas de aprobación"
              data-testid="btn-reglas-aprobacion">
              <Settings className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setTab("mias")}
          data-testid="tab-mis-solicitudes"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "mias" ? "border-[#FFDF00] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Mis solicitudes ({mine.length})
        </button>
        <button
          onClick={() => setTab("pendientes")}
          data-testid="tab-pendientes-aprobacion"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors relative ${tab === "pendientes" ? "border-[#FFDF00] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Pendientes de mi aprobación
          {pending.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full">{pending.length}</span>
          )}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          data-testid="filter-tipo-solicitud"
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 border border-slate-200">
          <option value="">Todos los tipos</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {tab === "mias" && (
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-estado-solicitud"
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 border border-slate-200">
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-semibold text-slate-700">
            {tab === "mias" ? "No tienes solicitudes todavía" : "No hay nada esperando tu aprobación"}
          </p>
          {tab === "mias" && (
            <button onClick={() => navigate("/ops/solicitudes/nueva")}
              className="mt-4 px-6 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors">
              Crear solicitud
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              onClick={() => openRow(r)}
              className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
              data-testid={`solicitud-row-${r.id}`}
            >
              <div className="flex items-center gap-4">
                <div>
                  <div className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
                    {TYPE_LABELS[r.type] || r.type}
                    {r.mirror_readonly && <ExternalLink className="w-3 h-3 text-slate-400" title="Ver en su módulo original" />}
                  </div>
                  <div className="text-xs text-slate-500">{r.requester_name}{r.title ? ` · ${r.title}` : ""}</div>
                  {r.needs_rule_configuration && (
                    <div className="text-[10px] text-red-600 font-semibold mt-0.5">⚠ Sin regla de aprobación configurada</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right text-sm">
                  <div className="text-xs text-slate-500">Monto</div>
                  <div className="font-bold">${(r.monto || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
                </div>
                <StatusBadge status={r.status} />
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
